import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Col, Input, Label, Row, Table } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import { getCustodyFileUrl, getTrainJourney, getTrainJourneys, getTrainRoutes, saveTrainJourney, setTrainJourneyMilestone, trainJourneyCars, undoTrainJourneyLoad } from '../devir-teslim/api';
import '../devir-teslim/devir-teslim.css';

// Sevkiyat İşlemleri > Tren Seferleri: a journey is our shipment number (TRN + year + 6 digits) on a train service
// defined in Tanımlamalar > Trenler, with the operator's journey number. Cars are put on it (paste or Excel), the
// loading list and the unload list from the destination are recorded as movements (custody records load point → train →
// unload point, the file kept as evidence). Loading start → unload finished is the journey transit SLA; its limit is
// the journey's ETD→ETA plus the rule's tolerance.
// Mirrors GemiPlanlama.js (ships) with train terminology.
const ROLE_GROUP = 43;

const STATUS = {
    PLANLANDI: ['Planlandı', 'secondary'], YUKLEMEDE: ['Yüklemede', 'info'], YOLDA: ['Yolda', 'primary'],
    BOSALTILIYOR: ['Boşaltılıyor', 'warning'], TAMAMLANDI: ['Tamamlandı', 'success'], IPTAL: ['İptal', 'dark'],
};
const MILESTONES = [
    ['loading', 'Yükleme başladı'], ['departed', 'Tren kalktı'], ['arrived', 'Tren vardı'],
    ['unload_started', 'Boşaltma başladı'], ['unload_finished', 'Boşaltma bitti'],
];
const ACTIONS = [
    ['PLAN', 'Sefere araç ekle (plan)'], ['LOAD', 'Yükleme listesi (limandan trene)'],
    ['UNLOAD', 'Boşaltma listesi (varış noktası)'], ['REMOVE', 'Plandan çıkar (yüklenmemiş)'],
    ['UNDO_LOAD', 'Yüklemeyi geri al (stoğa dön)'],
];
const EVENT = {
    SEFER_ACILDI: 'Sefer açıldı', SEFER_DEGISTI: 'Sefer bilgisi değişti', SEFER_IPTAL: 'Sefer iptal', ZAMAN: 'Sefer zamanı',
    LISTE_ITHAL: 'Tren listesi (Şase Tanımlama)', TERMINAL_YUKLEME: 'El terminaliyle yükleme', TAHLIYE: 'Boşaltma', TAHLIYE_GERI_ALINDI: 'Boşaltma geri alındı',
    LISTE_PLAN: 'Plana ekleme', LISTE_REMOVE: 'Plandan çıkarma', LISTE_LOAD: 'Yükleme listesi', LISTE_UNLOAD: 'Boşaltma listesi',
};

const fmt = (t) => (t ? moment(t).format('DD.MM.YYYY HH:mm') : '—');
const forInput = (t) => (t ? moment(t).format('YYYY-MM-DDTHH:mm') : '');
const err = (e, msg) => toast.error(e?.response?.data?.error || msg);

const EMPTY = { id: 0, train_service_id: '', operator_journey_no: '', load_location_id: '', unload_location_id: '',
    load_locode: '', unload_locode: '', etd: '', eta: '', note: '' };

const JourneyForm = ({ md, initial, onSaved, onCancel }) => {
    const [f, setF] = useState(initial);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const [routes, setRoutes] = useState([]);
    useEffect(() => {
        getTrainRoutes(0).then((d) => setRoutes(d.routes || [])).catch((e) => err(e, 'Rotalar alınamadı'));
    }, []);

    const selectedService = md.train_services.find((s) => String(s.id) === String(f.train_service_id));
    const operatorPartyID = selectedService ? selectedService.operator_party_id : null;
    const companyRoutes = operatorPartyID ? routes.filter((r) => String(r.operator_party_id) === String(operatorPartyID) && r.is_active) : [];
    const loadOptions = [];
    companyRoutes.forEach((r) => {
        if (!loadOptions.some((o) => String(o.id) === String(r.load_location_id))) {
            loadOptions.push({ id: r.load_location_id, name: r.load_location_name });
        }
    });
    const unloadOptions = [];
    companyRoutes.filter((r) => !f.load_location_id || String(r.load_location_id) === String(f.load_location_id)).forEach((r) => {
        if (!unloadOptions.some((o) => String(o.id) === String(r.unload_location_id))) {
            unloadOptions.push({ id: r.unload_location_id, name: r.unload_location_name });
        }
    });

    const handleServiceChange = (e) => {
        const val = e.target.value;
        setF((prev) => ({ ...prev, train_service_id: val, load_location_id: '', unload_location_id: '', load_locode: '', unload_locode: '' }));
    };

    useEffect(() => {
        setF((prev) => {
            let next = { ...prev };
            let changed = false;
            if (next.load_location_id && !next.load_locode) {
                const lp = md.places.find((p) => String(p.id) === String(next.load_location_id));
                if (lp && lp.locode) {
                    next.load_locode = lp.locode;
                    changed = true;
                }
            }
            if (next.unload_location_id && !next.unload_locode) {
                const dp = md.places.find((p) => String(p.id) === String(next.unload_location_id));
                if (dp && dp.locode) {
                    next.unload_locode = dp.locode;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [md.places]);

    const handlePlaceChange = (locationKey, locodeKey) => (e) => {
        const placeId = e.target.value;
        const selectedPlace = md.places.find((p) => String(p.id) === String(placeId));
        setF((prev) => {
            const next = {
                ...prev,
                [locationKey]: placeId,
                [locodeKey]: (selectedPlace && selectedPlace.locode) ? selectedPlace.locode : (placeId ? prev[locodeKey] : ''),
            };
            // Changing the load point can invalidate the previously-picked unload point (unload options are
            // filtered by load point). If the stale unload value is no longer among the valid options for the
            // new load point, clear it instead of silently submitting a now-invalid pair.
            if (locationKey === 'load_location_id' && next.unload_location_id) {
                const validUnloadIds = companyRoutes
                    .filter((r) => String(r.load_location_id) === String(placeId))
                    .map((r) => String(r.unload_location_id));
                if (!validUnloadIds.includes(String(next.unload_location_id))) {
                    next.unload_location_id = '';
                    next.unload_locode = '';
                }
            }
            return next;
        });
    };

    const save = async () => {
        try {
            const res = await saveTrainJourney({ ...f, train_service_id: Number(f.train_service_id) || 0, load_location_id: Number(f.load_location_id) || 0,
                unload_location_id: Number(f.unload_location_id) || 0 });
            toast.success(f.id ? 'Sefer güncellendi' : `Sefer açıldı: ${res.journey_no}`);
            onSaved(res.id);
        } catch (e) { err(e, 'Kaydedilemedi'); }
    };
    const place = (locationKey, locodeKey, options) => (
        <Input type="select" value={f[locationKey]} onChange={handlePlaceChange(locationKey, locodeKey)}>
            <option value="">— seçiniz —</option>
            {options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Input>
    );
    return (
        <div className="dt-draft mb-3">
            <Row>
                <Col md="3"><Label>Tren</Label>
                    <Input type="select" value={f.train_service_id} onChange={handleServiceChange}>
                        <option value="">— seçiniz —</option>
                        {md.train_services.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.operator_name}</option>)}
                    </Input>
                    <small className="text-muted">Listede yoksa: <Link to="/tanimlamalar/trenler">Tanımlamalar &gt; Trenler</Link></small></Col>
                <Col md="2"><Label>Tren Sefer No</Label><Input value={f.operator_journey_no} onChange={set('operator_journey_no')} placeholder="ör. 2644E" /></Col>
                <Col md="2"><Label>ETD (planlanan kalkış)</Label><Input type="datetime-local" value={f.etd} onChange={set('etd')} /></Col>
                <Col md="2"><Label>ETA (planlanan varış)</Label><Input type="datetime-local" value={f.eta} onChange={set('eta')} /></Col>
            </Row>
            <Row className="mt-2">
                {operatorPartyID && companyRoutes.length === 0 && <Col md="12" className="text-muted">
                    Bu işletmeci için tanımlı rota yok. Önce <Link to="/tanimlamalar/trenler">Tanımlamalar &gt; Trenler</Link>'den bir rota tanımlayın.
                </Col>}
                {(!operatorPartyID || companyRoutes.length > 0) && <>
                    <Col md="4"><Label>Yükleme noktası</Label>{place('load_location_id', 'load_locode', loadOptions)}</Col>
                    <Col md="1"><Label>UN/LOCODE</Label><Input value={f.load_locode} maxLength={6} placeholder="TRGEM" onChange={set('load_locode')} /></Col>
                    <Col md="4"><Label>Boşaltma noktası</Label>{place('unload_location_id', 'unload_locode', unloadOptions)}</Col>
                    <Col md="1"><Label>UN/LOCODE</Label><Input value={f.unload_locode} maxLength={6} placeholder="ITGOA" onChange={set('unload_locode')} /></Col>
                </>}
            </Row>
            <Row className="mt-2">
                <Col md="8"><Label>Not</Label><Input value={f.note} onChange={set('note')} /></Col>
                <Col md="4" className="d-flex align-items-end">
                    {f.id > 0 && <Label className="mr-3 mb-2"><Input type="checkbox" checked={!!f.cancelled} onChange={(e) => setF({ ...f, cancelled: e.target.checked })} /> Sefer iptal</Label>}
                    <Button color="primary" className="mr-1" onClick={save}>{f.id ? 'Kaydet' : 'Seferi aç'}</Button>
                    <Button color="secondary" onClick={onCancel}>Vazgeç</Button></Col>
            </Row>
        </div>
    );
};

const Milestone = ({ journey, code, name, canEdit, onSaved }) => {
    const [edit, setEdit] = useState(false);
    const [v, setV] = useState(forInput(journey[`${code}_at`]) || moment().format('YYYY-MM-DDTHH:mm'));
    const save = async (at) => {
        try { await setTrainJourneyMilestone(journey.id, code, at); setEdit(false); onSaved(); } catch (e) { err(e, 'Kaydedilemedi'); }
    };
    const at = journey[`${code}_at`];
    return (
        <Col className="dt-milestone border rounded p-2 mr-2 mb-2" style={{ minWidth: 150 }}>
            <div className="text-muted small">{name}</div>
            {!edit && <div><b>{fmt(at)}</b>
                {canEdit && journey.status !== 'IPTAL' && <Button size="xs" color="link" className="p-0 ml-2" onClick={() => setEdit(true)}>{at ? 'değiştir' : 'gir'}</Button>}</div>}
            {edit && <div>
                <Input bsSize="sm" type="datetime-local" value={v} onChange={(e) => setV(e.target.value)} />
                <Button size="sm" color="primary" className="mt-1 mr-1" onClick={() => save(v)}>Kaydet</Button>
                {at && <Button size="sm" color="danger" outline className="mt-1 mr-1" onClick={() => save('')}>Sil</Button>}
                <Button size="sm" color="secondary" className="mt-1" onClick={() => setEdit(false)}>Vazgeç</Button>
            </div>}
        </Col>
    );
};

const ListDownload = ({ ev }) => {
    if (!ev.list_id) return null;
    const open = async () => {
        try {
            const url = await getCustodyFileUrl(`list:${ev.list_id}`);
            const a = document.createElement('a');
            a.href = url; a.download = ev.file_name || `liste-${ev.list_id}`; a.click();
        } catch (e) { err(e, 'Liste indirilemedi'); }
    };
    return <Button size="sm" color="link" className="p-0" onClick={open}>{ev.file_name || 'liste'}</Button>;
};

// result of the last list, car by car; stays visible after the journey completes (the list form then closes)
const ListResult = ({ result }) => (
    <Table size="sm" className="mb-3">
        <thead><tr><th>Şase</th><th>Sonuç</th><th>Devir no</th><th>Açıklama</th></tr></thead>
        <tbody>{result.results.map((r) => (
            <tr key={r.chassis_no}><td>{r.chassis_no}</td>
                <td>{r.ok ? <Badge color={r.message ? 'warning' : 'success'}>{r.message ? 'uyarı' : 'tamam'}</Badge> : <Badge color="danger">hata</Badge>}</td>
                <td>{r.handover_no || ''}</td><td>{r.message || ''}</td></tr>
        ))}</tbody>
    </Table>
);

const CarsAction = ({ journey, onDone }) => {
    const [action, setAction] = useState('PLAN');
    const [text, setText] = useState('');
    const [file, setFile] = useState(null);
    const [at, setAt] = useState('');
    const [busy, setBusy] = useState(false);
    const vins = text.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
    const run = async () => {
        setBusy(true);
        try {
            if (action === 'UNDO_LOAD') {
                let done = 0; let failed = 0;
                const results = [];
                for (const vin of vins) {
                    try {
                        const r = await undoTrainJourneyLoad(journey.id, vin);
                        if (r && r.success) { done += 1; results.push({ chassis_no: vin, ok: true }); }
                        else { failed += 1; results.push({ chassis_no: vin, ok: false, message: 'başarısız' }); }
                    } catch (e) { failed += 1; results.push({ chassis_no: vin, ok: false, message: e?.response?.data?.error || 'hata' }); }
                }
                toast[failed ? 'warning' : 'success'](`${done} araç geri alındı${failed ? `, ${failed} hata` : ''}`);
                setText(''); setFile(null);
                onDone({ done, failed, results });
            } else {
                const res = await trainJourneyCars({ id: journey.id, action, chassis_nos: file ? [] : vins, at }, file);
                toast[res.failed ? 'warning' : 'success'](`${res.done} araç işlendi${res.failed ? `, ${res.failed} hata` : ''}`);
                setText(''); setFile(null);
                onDone(res);
            }
        } catch (e) { err(e, 'İşlenemedi'); }
        setBusy(false);
    };
    const withTime = action === 'LOAD' || action === 'UNLOAD';
    const supportsFile = action !== 'UNDO_LOAD';
    return (
        <div className="dt-draft mb-3">
            <Row>
                <Col md="4"><Label>İşlem</Label>
                    <Input type="select" value={action} onChange={(e) => setAction(e.target.value)}>
                        {ACTIONS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
                    </Input></Col>
                {supportsFile && <Col md="5"><Label>Excel / CSV listesi (şase sütunu; {withTime ? 'varsa tarih sütunu satırın zamanıdır' : 'başlık: Şase / VIN'})</Label>
                    <Input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files[0] || null)} key={file ? 'f' : 'nf'} /></Col>}
                {withTime && <Col md="3"><Label>{action === 'LOAD' ? 'Yükleme' : 'Boşaltma'} zamanı (tarihsiz satırlar; boş = şimdi)</Label>
                    <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} /></Col>}
            </Row>
            {!file && <Row className="mt-2"><Col>
                <Label>veya şaseleri yapıştırın ({vins.length})</Label>
                <Input type="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="her satıra bir şase" />
            </Col></Row>}
            <div className="mt-2"><Button color="primary" disabled={busy || (!file && vins.length === 0)} onClick={run}>Uygula</Button>
                <span className="text-muted ml-2 small">Liste, hareketin kaydı olarak saklanır (devir kayıtlarında kaynak: LİSTE).</span></div>
        </div>
    );
};

const JourneyDetail = ({ id, md, canEdit, onChanged }) => {
    const [d, setD] = useState(null);
    const [editing, setEditing] = useState(false);
    const [result, setResult] = useState(null);
    const load = useCallback(() => getTrainJourney(id).then(setD).catch((e) => err(e, 'Sefer alınamadı')), [id]);
    useEffect(() => { load(); }, [load]);
    if (!d) return null;
    const v = d.journey;
    const changed = () => { load(); onChanged(); };
    const transit = v.transit_hours != null ? `${v.transit_hours} saat (${(v.transit_hours / 24).toFixed(1)} gün)` : '—';
    const over = v.transit_hours != null && v.transit_hours > v.sla_hours;
    const active = d.cars.filter((c) => !c.removed_at);
    return (
        <Card><CardBody>
            <div className="d-flex align-items-center mb-2">
                <h5 className="mb-0 mr-3">{v.journey_no}</h5>
                <Badge color={STATUS[v.status][1]} className="mr-3">{STATUS[v.status][0]}</Badge>
                <span><b>{v.train_service_name}</b> · {v.operator_name} · tren sefer no <b>{v.operator_journey_no}</b></span>
                {canEdit && !editing && <Button size="sm" color="link" className="ml-auto" onClick={() => setEditing(true)}>Sefer bilgisini değiştir</Button>}
            </div>
            {editing && <JourneyForm md={md} initial={{ ...EMPTY, ...v, etd: forInput(v.etd), eta: forInput(v.eta),
                load_locode: v.load_locode || '', unload_locode: v.unload_locode || '', note: v.note || '' }}
                onSaved={() => { setEditing(false); changed(); }} onCancel={() => setEditing(false)} />}
            <div className="mb-2">
                {v.load_location}{v.load_locode ? ` (${v.load_locode})` : ''} → {v.unload_location}{v.unload_locode ? ` (${v.unload_locode})` : ''}
                <span className="ml-3 text-muted">ETD {fmt(v.etd)} · ETA {fmt(v.eta)}</span>
                {v.note && <span className="ml-3 text-muted">Not: {v.note}</span>}
            </div>
            <Row className="mx-0">
                {MILESTONES.map(([code, name]) => <Milestone key={code + (v[`${code}_at`] || '')} journey={v} code={code} name={name} canEdit={canEdit} onSaved={changed} />)}
            </Row>
            <div className="mb-3">
                Sefer süresi (yükleme başlangıcı → boşaltma bitişi): <b className={over ? 'text-danger' : ''}>{transit}</b>
                <span className="text-muted"> · SLA {v.sla_hours} saat{v.etd && v.eta ? ' (ETD→ETA + tolerans)' : ''}{v.unload_finished_at ? '' : (v.loading_at ? ' · sürüyor' : '')}</span>
                <span className="ml-3">Planlı <b>{v.planned}</b> · Yüklenen <b>{v.loaded}</b> · Boşaltılan <b>{v.unloaded}</b></span>
            </div>
            {canEdit && v.status !== 'IPTAL' && v.status !== 'TAMAMLANDI' && <CarsAction journey={v} onDone={(res) => { setResult(res); changed(); }} />}
            {result && <ListResult result={result} />}
            <Table size="sm" bordered responsive>
                <thead><tr><th>Şase</th><th>Marka / model</th><th>Şu an (depo / bölge)</th><th>Sevk emri hedefi</th><th>Plana alındı</th>
                    <th>Yüklendi</th><th>Boşaltıldı</th></tr></thead>
                <tbody>
                    {active.length === 0 && <tr><td colSpan={7} className="text-muted">Seferde araç yok.</td></tr>}
                    {d.cars.map((c) => (
                        <tr key={c.chassis_no} className={c.removed_at ? 'text-muted' : ''} style={c.removed_at ? { textDecoration: 'line-through' } : {}}>
                            <td>{c.chassis_no}</td>
                            <td>{c.brand} {c.model}</td><td>{c.place || '—'}</td><td>{c.order_destination || '—'}</td>
                            <td>{fmt(c.planned_at)}</td>
                            <td>{fmt(c.loaded_at)}{c.load_handover_no && <div className="small text-muted">{c.load_handover_no}</div>}</td>
                            <td>{fmt(c.unloaded_at)}{c.unload_handover_no && <div className="small text-muted">{c.unload_handover_no}</div>}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <h6 className="mt-3">Sefer geçmişi</h6>
            <Table size="sm" responsive>
                <thead><tr><th>Zaman</th><th>Kim</th><th>Ne</th><th>Ayrıntı</th><th>Liste</th></tr></thead>
                <tbody>{d.events.map((e, i) => (
                    <tr key={i}><td>{fmt(e.at)}</td><td>{e.by_name}</td><td>{EVENT[e.kind] || e.kind}</td>
                        <td className="small">{e.kind.startsWith('SEFER_') ? '' : e.detail}</td><td><ListDownload ev={e} /></td></tr>
                ))}</tbody>
            </Table>
        </CardBody></Card>
    );
};

const TrenPlanlama = () => {
    const { hasRole } = useAuth(ROLE_GROUP);
    const canEdit = hasRole('tren_sefer_write');
    const [onlyOpen, setOnlyOpen] = useState(true);
    const [md, setMd] = useState({ journeys: [], train_services: [], places: [] });
    const [adding, setAdding] = useState(false);
    const [selected, setSelected] = useState(null);
    const load = useCallback(() => getTrainJourneys(onlyOpen).then(setMd).catch((e) => err(e, 'Seferler alınamadı')), [onlyOpen]);
    useEffect(() => { load(); }, [load]);
    const shown = md.journeys;
    return (
        <>
            <Breadcrumb parent="Sevkiyat İşlemleri" current="Tren Seferleri" />
            <Card><CardBody>
                <div className="d-flex align-items-center mb-2" style={{ gap: 16 }}>
                    <span className="text-muted">Sefer = tren + tren sefer no; bizim numaramız TRN+yıl+6 hane. Tren, Tanımlamalar &gt; Trenler'den seçilir.</span>
                    <Label className="mb-0 ml-auto text-nowrap"><Input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> Sadece açık seferler</Label>
                    {canEdit && !adding && <Button color="primary" onClick={() => setAdding(true)}>+ Yeni sefer</Button>}
                </div>
                {adding && <JourneyForm md={md} initial={{ ...EMPTY }} onCancel={() => setAdding(false)}
                    onSaved={(id) => { setAdding(false); setSelected(id); load(); }} />}
                <Table size="sm" hover responsive>
                    <thead><tr><th>Sefer no</th><th>Tren</th><th>Tren Sefer No</th><th>Güzergâh</th><th>ETD</th><th>ETA</th><th>Durum</th>
                        <th>Planlı / yüklenen / boşaltılan</th><th>Sefer süresi</th></tr></thead>
                    <tbody>
                        {shown.length === 0 && <tr><td colSpan={9} className="text-muted">Sefer yok.</td></tr>}
                        {shown.map((v) => (
                            <tr key={v.id} onClick={() => setSelected(v.id)} style={{ cursor: 'pointer' }} className={selected === v.id ? 'table-active' : ''}>
                                <td><b>{v.journey_no}</b></td><td>{v.train_service_name}<div className="small text-muted">{v.operator_name}</div></td>
                                <td>{v.operator_journey_no}</td><td>{v.load_location} → {v.unload_location}</td>
                                <td>{fmt(v.etd)}</td><td>{fmt(v.eta)}</td>
                                <td><Badge color={STATUS[v.status][1]}>{STATUS[v.status][0]}</Badge></td>
                                <td>{v.planned} / {v.loaded} / {v.unloaded}</td>
                                <td className={v.transit_hours > v.sla_hours ? 'text-danger' : ''}>{v.transit_hours != null ? `${v.transit_hours} sa` : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </CardBody></Card>
            {selected && <JourneyDetail key={selected} id={selected} md={md} canEdit={canEdit} onChanged={load} />}
        </>
    );
};

export default TrenPlanlama;
